import { memo, useState } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

let _showLabels = false;

const TAG_COLORS = {
  'Principal (2)': '#006837',
  'Significant (1)': '#3B9B61',
  'Not Targeted (0)': '#94a3b8',
};

export const InnovationImpactAreaChart = memo(function InnovationImpactAreaChart() {
  const { impactAreaData, totalCount, innovationResults } = useInnovationData();
  const [showLabels, setShowLabels] = useState(_showLabels);
  const yAxisWidth = useResponsiveYAxisWidth(160);
  if (!impactAreaData || totalCount === 0) return (
    <ChartWrapper title="Innovation Impact Areas">
      <NoChartData message="No innovation results to analyze" detail="This chart shows how innovation results are tagged across the five CGIAR impact areas (Gender, Climate, Nutrition, Biodiversity, Poverty) using the tag-level fields (Principal / Significant / Not Targeted)." />
    </ChartWrapper>
  );

  const data = Object.entries(impactAreaData).map(([key, val]) => ({
    area: val.label,
    'Principal (2)': val.Principal,
    'Significant (1)': val.Significant,
    'Not Targeted (0)': val['Not Targeted'],
  }));

  const exportData = data.map(d => ({
    'Impact Area': d.area,
    'Principal (2)': d['Principal (2)'],
    'Significant (1)': d['Significant (1)'],
    'Not Targeted (0)': d['Not Targeted (0)'],
  }));

  return (
    <ChartWrapper title="Innovation Results by Impact Area" info="How innovation results are tagged across the five CGIAR Impact Areas at Principal, Significant, or Not Targeted levels." chartData={exportData} exportFilename="innovation-impact-area" sourceResults={innovationResults}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">
          Tagging of {totalCount} innovation results against CGIAR impact areas (Principal, Significant, or Not Targeted).
        </p>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none whitespace-nowrap ml-4">
          <input type="checkbox" checked={showLabels} onChange={e => { _showLabels = e.target.checked; setShowLabels(e.target.checked); }} className="rounded border-gray-300" />
          Show counts
        </label>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 30 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis type="category" dataKey="area" tick={{ fontSize: yAxisWidth < 160 ? 9 : 11 }} width={yAxisWidth} />
          <Tooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const lookup = {};
            payload.forEach(p => { lookup[p.dataKey] = p; });
            const order = ['Not Targeted (0)', 'Significant (1)', 'Principal (2)'];
            return (
              <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{label}</div>
                {order.map(key => lookup[key] && (
                  <div key={key} style={{ color: lookup[key].color }}>{key}: {lookup[key].value?.toLocaleString()}</div>
                ))}
              </div>
            );
          }} />
          <Legend content={() => {
            const entries = [
              { label: 'Not Targeted (0)', color: TAG_COLORS['Not Targeted (0)'] },
              { label: 'Significant (1)', color: TAG_COLORS['Significant (1)'] },
              { label: 'Principal (2)', color: TAG_COLORS['Principal (2)'] },
            ];
            return (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 4 }}>
                {entries.map(e => (
                  <span key={e.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
                    <span style={{ width: 10, height: 10, background: e.color, borderRadius: 2, display: 'inline-block' }} />
                    {e.label}
                  </span>
                ))}
              </div>
            );
          }} />
          <Bar dataKey="Not Targeted (0)" stackId="a" fill={TAG_COLORS['Not Targeted (0)']}>
            {showLabels && <LabelList dataKey="Not Targeted (0)" position="center" fill="#fff" fontSize={11} fontWeight={700} formatter={v => v || ''} />}
          </Bar>
          <Bar dataKey="Significant (1)" stackId="a" fill={TAG_COLORS['Significant (1)']}>
            {showLabels && <LabelList dataKey="Significant (1)" position="center" fill="#fff" fontSize={11} fontWeight={700} formatter={v => v || ''} />}
          </Bar>
          <Bar dataKey="Principal (2)" stackId="a" fill={TAG_COLORS['Principal (2)']}>
            {showLabels && <LabelList dataKey="Principal (2)" position="center" fill="#fff" fontSize={11} fontWeight={700} formatter={v => v || ''} />}
            <LabelList content={(props) => {
              const { x, y, width, height, index } = props;
              const entry = data[index];
              const total = (entry['Principal (2)'] || 0) + (entry['Significant (1)'] || 0) + (entry['Not Targeted (0)'] || 0);
              return (
                <text x={x + width + 4} y={y + height / 2} fill="#666" fontSize={11} dominantBaseline="middle">
                  {total}
                </text>
              );
            }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
